import React, { FunctionComponent, useEffect, useMemo } from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { IGeneratedSecrReportResponse, ISecrEnergySourceLine } from '../../../../shared/services/generated-report/generated-report.model';
import type { SecrSourceMetricField } from './secr-source-applicability';
import { formatSecrSourceDataCell, formatSecrSourceTotalCell } from './secr-source-matrix-display';
import { SECR_EM_DASH } from './secr-number-format';
import {
  buildSecrSourceTotalLine,
  displaySourceLabel,
  logSecrBreakdownValidationDev,
  shouldShowUnmappedRow,
  validateSecrBreakdown,
} from './secr-source-matrix.utils';
import { SECR_REPORT_LABEL_COLUMN_PERCENT } from './secr-summary-table.utils';

function periodChipSx(isPrimary: boolean) {
  return {
    fontWeight: 600,
    fontSize: '0.8125rem',
    color: isPrimary ? 'primary.dark' : 'common.black',
    ...(isPrimary ? { backgroundColor: 'secondary.light' } : {}),
  } as const;
}

const subHeaderCellSx = {
  fontWeight: 600,
  fontSize: '0.8125rem',
  color: 'common.black',
  textAlign: 'right',
  borderBottomColor: 'primary.dark',
  borderBottomWidth: '3px',
  borderBottomStyle: 'solid',
  py: 0.45,
} as const;

const metricGroupHeaderSx = {
  borderBottom: 'none',
  fontWeight: 600,
  fontSize: '0.8125rem',
  color: 'common.black',
  textAlign: 'center',
  py: 0.45,
} as const;

function hasAnyMarketS2(lines: ISecrEnergySourceLine[] | undefined): boolean {
  if (!lines?.length) return false;
  return lines.some((l) => {
    const g = Number(l.global?.scope2MarketTco2e) || 0;
    const u = Number(l.uk?.scope2MarketTco2e) || 0;
    const o = Number(l.overseas?.scope2MarketTco2e) || 0;
    return g !== 0 || u !== 0 || o !== 0;
  });
}

function SecrSourceMatrixMetricBand({
  line,
  field,
  dataLines,
  showOverseas,
  backgroundColor,
  isTotal,
}: {
  line: ISecrEnergySourceLine;
  field: SecrSourceMetricField;
  dataLines: ISecrEnergySourceLine[];
  showOverseas: boolean;
  backgroundColor: string;
  isTotal: boolean;
}) {
  const cell = (seg: 'uk' | 'overseas' | 'global') => {
    const v = line[seg][field] as number | null | undefined;
    return isTotal
      ? formatSecrSourceTotalCell(dataLines, field, v)
      : formatSecrSourceDataCell(line.key, field, v);
  };

  const cellSx = (text: string) => ({
    backgroundColor,
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums' as const,
    fontWeight: isTotal ? 700 : 400,
    py: 0.55,
    px: 1,
    color: text === SECR_EM_DASH ? 'text.secondary' : 'primary.dark',
  });

  const u = cell('uk');
  const o = showOverseas ? cell('overseas') : '';
  const g = showOverseas ? cell('global') : '';

  return (
    <>
      <TableCell scope="row" sx={cellSx(u)}>
        {u}
      </TableCell>
      {showOverseas && (
        <>
          <TableCell scope="row" sx={cellSx(o)}>
            {o}
          </TableCell>
          <TableCell scope="row" sx={cellSx(g)}>
            {g}
          </TableCell>
        </>
      )}
    </>
  );
}

export const ReportingSecrSourceMatrix: FunctionComponent<{ report: IGeneratedSecrReportResponse }> = ({ report }) => {
  const periodsOrdered = useMemo(() => {
    const primary = report.reportDetails.find((d) => d.isPrimaryReportPeriod);
    const rest = report.reportDetails.filter((d) => !d.isPrimaryReportPeriod).sort((a, b) => Number(b.reportPeriodName) - Number(a.reportPeriodName));
    return primary ? [primary, ...rest] : rest;
  }, [report.reportDetails]);

  const showMarketS2Col = useMemo(
    () => report.reportDetails.some((d) => hasAnyMarketS2(d.energyBreakdownBySource)),
    [report.reportDetails]
  );

  useEffect(() => {
    for (const period of periodsOrdered) {
      const raw = period.energyBreakdownBySource ?? [];
      const dataLines = raw.filter((l) => l.key !== 'total').filter(shouldShowUnmappedRow);
      if (dataLines.length === 0) continue;
      const v = validateSecrBreakdown(period, dataLines, report.includeGlobal);
      logSecrBreakdownValidationDev(period.reportPeriodName, v);
    }
  }, [periodsOrdered, report.includeGlobal]);

  const colSpan = report.includeGlobal ? 3 : 1;
  const metricGroups = showMarketS2Col ? 4 : 3;
  const sourceMatrixDataColumnCount = metricGroups * colSpan;
  const sourceLabelColumnPercent = SECR_REPORT_LABEL_COLUMN_PERCENT;
  const sourceDataColumnPercent =
    sourceMatrixDataColumnCount > 0 ? (100 - sourceLabelColumnPercent) / sourceMatrixDataColumnCount : 100 - sourceLabelColumnPercent;

  const dataBandColor = (isPrimary: boolean) => (isPrimary ? 'secondary.light' : 'white');

  return (
    <Box sx={{ mt: 1 }}>
      {periodsOrdered.map((period) => {
        const rawLines = period.energyBreakdownBySource ?? [];
        const dataLines = rawLines.filter((l) => l.key !== 'total').filter(shouldShowUnmappedRow);
        if (!dataLines.length) return null;

        const totalLine = buildSecrSourceTotalLine(dataLines);
        const rows: ISecrEnergySourceLine[] = [...dataLines, totalLine];

        const title = period.isPrimaryReportPeriod
          ? `${period.reportPeriodName} - Current Year`
          : `${period.reportPeriodName}${period.isBaseYearReportPeriod ? ' - Base Year' : ''}`;
        const band = dataBandColor(period.isPrimaryReportPeriod);

        return (
          <Box key={period.reportPeriodId} sx={{ mb: 4 }}>
            <TableContainer>
              <Table
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  borderCollapse: 'collapse',
                  '& thead .MuiTableCell-root': { py: 0.4 },
                  '& tbody tr:first-of-type .MuiTableCell-root': { pt: 0.35 },
                }}
              >
                <colgroup>
                  <col style={{ width: `${sourceLabelColumnPercent}%` }} />
                  {Array.from({ length: sourceMatrixDataColumnCount }).map((_, i) => (
                    <col key={i} style={{ width: `${sourceDataColumnPercent}%` }} />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        verticalAlign: 'top',
                        pt: 0.85,
                        pb: 0.5,
                        pl: 1.75,
                        pr: 1,
                        borderBottom: '3px solid',
                        borderColor: 'primary.dark',
                      }}
                    >
                      <Chip label={title} size="small" sx={{ ...periodChipSx(period.isPrimaryReportPeriod), mb: 0.75, maxWidth: '100%' }} />
                      <Box
                        component="div"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          color: 'common.black',
                          mt: 0.25,
                        }}
                      >
                        Source
                      </Box>
                    </TableCell>
                    <TableCell align="center" colSpan={colSpan} sx={metricGroupHeaderSx}>
                      Energy (kWh)
                    </TableCell>
                    <TableCell align="center" colSpan={colSpan} sx={metricGroupHeaderSx}>
                      Scope 1 (tCO<sub>2</sub>e)
                    </TableCell>
                    <TableCell align="center" colSpan={colSpan} sx={metricGroupHeaderSx}>
                      Scope 2 (Location-based) (tCO<sub>2</sub>e)
                    </TableCell>
                    {showMarketS2Col && (
                      <TableCell align="center" colSpan={colSpan} sx={metricGroupHeaderSx}>
                        Scope 2 (Market-based) (tCO<sub>2</sub>e)
                      </TableCell>
                    )}
                  </TableRow>
                  <TableRow>
                    {Array.from({ length: showMarketS2Col ? 4 : 3 }).map((_, mi) =>
                      report.includeGlobal ? (
                        <React.Fragment key={mi}>
                          <TableCell align="right" sx={subHeaderCellSx}>
                            UK
                          </TableCell>
                          <TableCell align="right" sx={subHeaderCellSx}>
                            Overseas
                          </TableCell>
                          <TableCell align="right" sx={subHeaderCellSx}>
                            Total
                          </TableCell>
                        </React.Fragment>
                      ) : (
                        <TableCell key={mi} align="right" sx={subHeaderCellSx}>
                          UK
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((line) => {
                    const isTotal = line.key === 'total';
                    const label = isTotal ? line.label : displaySourceLabel(line.key, line.label);
                    return (
                      <TableRow
                        key={`${period.reportPeriodId}-${line.key}`}
                        sx={{
                          ...(isTotal
                            ? {
                                '& .MuiTableCell-root': {
                                  borderTop: '3px solid',
                                  borderTopColor: 'primary.dark',
                                },
                              }
                            : {
                                '& .MuiTableCell-root': {
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                },
                              }),
                        }}
                      >
                        <TableCell
                          scope="row"
                          sx={{
                            textAlign: 'left',
                            fontWeight: 400,
                            color: 'text.primary',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            verticalAlign: 'middle',
                            py: 0.55,
                            pl: 1.75,
                            pr: 1,
                          }}
                        >
                          {label}
                        </TableCell>
                        <SecrSourceMatrixMetricBand
                          line={line}
                          field="energyKwh"
                          dataLines={dataLines}
                          showOverseas={!!report.includeGlobal}
                          backgroundColor={band}
                          isTotal={isTotal}
                        />
                        <SecrSourceMatrixMetricBand
                          line={line}
                          field="scope1Tco2e"
                          dataLines={dataLines}
                          showOverseas={!!report.includeGlobal}
                          backgroundColor={band}
                          isTotal={isTotal}
                        />
                        <SecrSourceMatrixMetricBand
                          line={line}
                          field="scope2LocationTco2e"
                          dataLines={dataLines}
                          showOverseas={!!report.includeGlobal}
                          backgroundColor={band}
                          isTotal={isTotal}
                        />
                        {showMarketS2Col && (
                          <SecrSourceMatrixMetricBand
                            line={line}
                            field="scope2MarketTco2e"
                            dataLines={dataLines}
                            showOverseas={!!report.includeGlobal}
                            backgroundColor={band}
                            isTotal={isTotal}
                          />
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </Box>
  );
};
